'use client';

import useMolocoLogger from '@hooks/useMolocoLogger';
import { MolocoAdLPProduct } from '@models/moloco';
import { sendMolocoImpTracker } from '@tags/moloco';
import PrdcUnitItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/PrdcUnitItem';
import { useCallback, useEffect, useRef } from 'react';
import useGlobalState from '@hooks/useGlobalState';
import { Product } from '@wconcept/wconcept-common/models/display/product';
import useHistoryRouter from '@hooks/useLinkTo';
import { addAsIsMWebDomain } from '@utils/domainUtil';
import { getExclusiveEmblemProp } from '@utils/productExclusiveUtil';

interface MolocoRecLPProductProps {
  molocoProduct: MolocoAdLPProduct;
  displayIndex: number;
  isNativeAppBoth: boolean;
  onClickItem?: (value: Product, no: number) => void;
  onClickHeart?: (
    value: Product,
    active: boolean,
    index: number,
    type: string
  ) => void;
  showExclusiveEmblem?: boolean;
  /** 모바일 가상 리스트 열 개수. 3열일 때 정상가·하트수 미전달 */
  mobileVirtualColumnCount?: number;
}

function MolocoRecLpProduct({
  molocoProduct,
  displayIndex,
  isNativeAppBoth,
  onClickItem,
  onClickHeart,
  showExclusiveEmblem,
  mobileVirtualColumnCount = 2,
}: MolocoRecLPProductProps) {
  const { isMobile, assistData, myHeartList } = useGlobalState<any>();

  const { linkTo } = useHistoryRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasImpressedRef = useRef(false);
  const { sendMolocoDecisionClickTracker } = useMolocoLogger();
  const v = molocoProduct.product;
  const hideCompactMeta = mobileVirtualColumnCount === 3;

  // 노출 트래킹 (IntersectionObserver) - useRef로 한 번만 실행
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasImpressedRef.current) {
          hasImpressedRef.current = true;
          const impUrl = molocoProduct?.impTrackerList?.[0];
          if (impUrl) {
            sendMolocoImpTracker(isNativeAppBoth, [impUrl]);
          }
        }
      },
      { threshold: 1.0 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isNativeAppBoth, molocoProduct?.impTrackerList]);

  const onClickMolocoRecLpProduct = useCallback(
    async (product: Product) => {
      sendMolocoDecisionClickTracker(molocoProduct?.clickTrackerList?.[0]);
      onClickItem?.(product, displayIndex);

      const url = product.webViewUrl || product.landingUrl;
      if (url) {
        linkTo.push({
          url: await addAsIsMWebDomain(url),
        });
      }
    },
    [
      molocoProduct,
      displayIndex,
      onClickItem,
      linkTo,
      sendMolocoDecisionClickTracker,
    ]
  );

  return (
    <PrdcUnitItem
      ref={containerRef}
      key={v?.itemCd}
      isAd
      isDesktop={!isMobile}
      isLogin={assistData.isLogin}
      itemCd={v?.itemCd}
      statusCd={v?.statusCd}
      finalPrice={v?.finalPrice}
      customerPrice={hideCompactMeta ? undefined : v?.customerPrice}
      finalDiscountRate={v?.finalDiscountRate}
      reviewScore={v?.reviewScore}
      reviewCnt={v?.reviewCnt}
      hideHeartCnt={hideCompactMeta}
      heartCnt={hideCompactMeta ? undefined : v?.heartCnt}
      saleTag={v?.saleTag}
      infoTags={v?.infoTags}
      exclusive={getExclusiveEmblemProp(v, showExclusiveEmblem)}
      imageUrl={v?.imageUrlMobile || v?.productImageUrl}
      brandCd={v?.brandCd}
      brandNameKr={v?.brandNameKr}
      itemNameSub={v?.itemNameSub}
      itemName={v?.itemName}
      todayDeliveryTag={v?.todayDeliveryTag}
      colors={v?.colors}
      myHeartItem={v?.itemCd ? !!myHeartList?.product?.has(v?.itemCd) : false}
      onClick={() => onClickMolocoRecLpProduct(v)}
      onClickHeart={async (itemCd, active) =>
        onClickHeart?.(v, active, displayIndex, 'molocoLp')
      }
    />
  );
}

export default MolocoRecLpProduct;
